/**
 * EFFET : rendu WebGL du miroir déformant.
 * 1) la vidéo en plein cadre ; 2) le maillage du visage par-dessus, avec pour
 * coordonnées de texture les points D'ORIGINE et pour sommets les points
 * DÉFORMÉS : la peau « suit » la déformation, sans décalage (même image que
 * celle analysée par MediaPipe).
 * Si le contexte WebGL est perdu (pilote graphique), le rendu reprend dès qu'il est restauré.
 */
import type { Point3 } from '../detection/types';
import { FACE_TRIANGLES } from './faceTriangles';

const MESH_SIZE = 468;

const VERTEX = `
attribute vec2 a_pos;
attribute vec2 a_uv;
varying vec2 v_uv;
void main() {
  v_uv = a_uv;
  gl_Position = vec4(a_pos.x * 2.0 - 1.0, 1.0 - a_pos.y * 2.0, 0.0, 1.0);
}`;

const FRAGMENT = `
precision mediump float;
uniform sampler2D u_tex;
varying vec2 v_uv;
void main() {
  gl_FragColor = texture2D(u_tex, v_uv);
}`;

/** Image à déformer : la copie analysée (webcam) ou, à défaut, la vidéo. */
export type MirrorImage = HTMLVideoElement | HTMLCanvasElement;

type Draw = (
  video: MirrorImage,
  original: readonly Point3[] | null,
  deformed: readonly Point3[] | null,
) => void;

export interface MirrorRenderer {
  /** `false` : pas de WebGL, la vidéo est dessinée sans déformation (canvas 2D). */
  readonly deforms: boolean;
  readonly draw: Draw;
}

function compile(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS))
    throw new Error(gl.getShaderInfoLog(shader) ?? 'shader');
  return shader;
}

function fitCanvas(canvas: HTMLCanvasElement, video: MirrorImage): boolean {
  const [w, h] =
    video instanceof HTMLVideoElement
      ? [video.videoWidth, video.videoHeight]
      : [video.width, video.height];
  if (!w || !h) return false;
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  return true;
}

function create2dFallback(canvas: HTMLCanvasElement): MirrorRenderer {
  const ctx = canvas.getContext('2d');
  return {
    deforms: false,
    draw(video) {
      if (ctx && fitCanvas(canvas, video)) ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    },
  };
}

export function createMirrorRenderer(canvas: HTMLCanvasElement): MirrorRenderer {
  const gl = canvas.getContext('webgl', { alpha: false, antialias: true });
  if (!gl) return create2dFallback(canvas);
  let draw = setupGl(gl, canvas);
  if (!draw) return create2dFallback(canvas);

  let lost = false;
  canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault(); // Sans ça, le navigateur ne restaure jamais le contexte.
    lost = true;
  });
  canvas.addEventListener('webglcontextrestored', () => {
    draw = setupGl(gl, canvas);
    lost = false;
  });
  return {
    deforms: true,
    draw(video, original, deformed) {
      if (!lost) draw?.(video, original, deformed);
    },
  };
}

/** Programme, tampons et texture ; `null` si le GPU refuse (shaders). */
function setupGl(gl: WebGLRenderingContext, canvas: HTMLCanvasElement): Draw | null {
  const program = gl.createProgram();
  try {
    gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX));
    gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT));
  } catch {
    return null;
  }
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  gl.useProgram(program);

  const aPos = gl.getAttribLocation(program, 'a_pos');
  const aUv = gl.getAttribLocation(program, 'a_uv');
  const posBuffer = gl.createBuffer();
  const uvBuffer = gl.createBuffer();
  const quadIndex = gl.createBuffer();
  const meshIndex = gl.createBuffer();
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, meshIndex);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, FACE_TRIANGLES, gl.STATIC_DRAW);
  const QUAD = new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]);
  const QUAD_INDEX = new Uint16Array([0, 1, 2, 2, 1, 3]);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, quadIndex);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, QUAD_INDEX, gl.STATIC_DRAW);

  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  for (const [p, v] of [
    [gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE],
    [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE],
    [gl.TEXTURE_MIN_FILTER, gl.LINEAR],
    [gl.TEXTURE_MAG_FILTER, gl.LINEAR],
  ] as const) {
    gl.texParameteri(gl.TEXTURE_2D, p, v);
  }

  const positions = new Float32Array(MESH_SIZE * 2);
  const uvs = new Float32Array(MESH_SIZE * 2);

  const bindAttribute = (buffer: WebGLBuffer, location: number, data: Float32Array): void => {
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(location, 2, gl.FLOAT, false, 0, 0);
  };

  return (video, original, deformed) => {
    if (!fitCanvas(canvas, video)) return;
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    // RGBA : le format d'envoi le mieux pris en charge (RGB corrompt l'image sur certains GPU).
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);

    // 1) vidéo en plein cadre
    bindAttribute(posBuffer, aPos, QUAD);
    bindAttribute(uvBuffer, aUv, QUAD);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, quadIndex);
    gl.drawElements(gl.TRIANGLES, QUAD_INDEX.length, gl.UNSIGNED_SHORT, 0);

    // 2) visage déformé par-dessus
    if (!original || !deformed || original.length < MESH_SIZE || deformed.length < MESH_SIZE)
      return;
    for (let i = 0; i < MESH_SIZE; i++) {
      const o = original[i] as Point3;
      const d = deformed[i] as Point3;
      uvs[2 * i] = o.x;
      uvs[2 * i + 1] = o.y;
      positions[2 * i] = d.x;
      positions[2 * i + 1] = d.y;
    }
    bindAttribute(posBuffer, aPos, positions);
    bindAttribute(uvBuffer, aUv, uvs);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, meshIndex);
    gl.drawElements(gl.TRIANGLES, FACE_TRIANGLES.length, gl.UNSIGNED_SHORT, 0);
  };
}
